'use client';

import React from 'react';
import { Badge } from '@/components/ui/badge';
import { ButtonCta } from '@/components/ui/ButtonCta';
import { ArrowRight, Download, Phone, TrendingUp, Zap, Shield, DollarSign } from '@/lib/icons';
import { BlogNewsletterForm } from '@/components/blog/BlogNewsletterForm';

interface BlogPost {
  title: string;
  slug: string;
  category: string;
}

interface BlogSidebarProps {
  currentSlug: string;
  category?: string;
  allBlogPosts?: BlogPost[];
  // CTA Banner customization
  ctaHeadline?: string;
  ctaDescription?: string; 
  ctaButtonText?: string;
  ctaButtonLink?: string;
  ctaSubtext?: string;
}

export function BlogSidebar({ 
  currentSlug, 
  category,
  allBlogPosts = [],
  ctaHeadline = "Generate Leads. Build Partnerships. Close Loans.",
  ctaDescription = "Get the exact playbook 5,247+ mortgage professionals use to create sustainable pipeline and agent partnerships that actually produce.",
  ctaButtonText = "Request Strategy Call", 
  ctaButtonLink = "/mortgage-lead-generation-demo",
  ctaSubtext = "45-minute session • No BS assessment"
}: BlogSidebarProps) {
  // Get actual blog posts (excluding current one)
  const popularPosts = allBlogPosts
    .filter(post => post.slug !== currentSlug)
    .slice(0, 3);

  // Generate dynamic categories from actual posts
  const categoryMap = new Map<string, number>();
  allBlogPosts.forEach(post => {
    const count = categoryMap.get(post.category) || 0;
    categoryMap.set(post.category, count + 1);
  });
  
  const categories = Array.from(categoryMap.entries())
    .map(([name, count]) => ({ name, count }))
    .filter(cat => cat.count > 0)
    .sort((a, b) => b.count - a.count);

  return (
    <div className="space-y-8">
      {/* Primary CTA - Demo Request */}
      <div className="bg-primary rounded-xl p-6 text-white shadow-xl">
        <div className="mb-4">
          <Zap className="w-10 h-10 text-yellow-300" />
        </div>
        <h3 className="text-2xl font-bold mb-3">
          {ctaHeadline}
        </h3>
        <p className="text-white/90 mb-6 text-sm leading-relaxed">
          {ctaDescription}
        </p>
        <ButtonCta 
          href={ctaButtonLink}
          variant="primary"
          className="w-full"
          showArrow={true}
        >
          {ctaButtonText}
        </ButtonCta>
        <p className="text-xs text-white/70 mt-3 text-center">
          {ctaSubtext}
        </p>
      </div>

      {/* Secondary CTA - Free Guide Download */}
      <div className="bg-gray-50 border border-gray-200 rounded-xl p-6">
        <div className="flex items-start mb-4">
          <Download className="w-8 h-8 text-primary mr-3 flex-shrink-0" />
          <div>
            <h3 className="text-lg font-bold text-gray-900">
              Free Guide: Mortgage Lead Generation Playbook
            </h3>
          </div>
        </div>
        <p className="text-gray-600 mb-4 text-sm">
          Get the exact strategies our top clients use to generate 50+ exclusive leads per month.
        </p>
        <ul className="space-y-2 mb-6">
          <li className="flex items-start text-sm text-gray-700">
            <span className="text-green-500 mr-2">✓</span>
            <span>15 proven lead generation strategies</span>
          </li>
          <li className="flex items-start text-sm text-gray-700">
            <span className="text-green-500 mr-2">✓</span>
            <span>RESPA compliance checklist</span>
          </li>
          <li className="flex items-start text-sm text-gray-700">
            <span className="text-green-500 mr-2">✓</span>
            <span>ROI calculation templates</span>
          </li>
        </ul>
        <a 
          href="/guide/mortgage-lead-generation-apocalypse"
          className="inline-flex items-center justify-center w-full px-4 py-2.5 bg-primary text-white font-medium rounded-lg hover:bg-primary-dark transition-colors"
        >
          Download Free Guide
          <Download className="ml-2 w-4 h-4" />
        </a>
      </div>


      {/* Newsletter Signup */}
      <div className="bg-primary/5 border border-primary/20 rounded-xl p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-3">
          Weekly Marketing Tips
        </h3>
        <p className="text-sm text-gray-600 mb-4">
          Get exclusive strategies delivered to your inbox every Tuesday.
        </p>
        <BlogNewsletterForm variant="sidebar" />
        <p className="text-xs text-gray-500 mt-3">
          Join 5,247+ mortgage professionals. Unsubscribe anytime.
        </p>
      </div>

      {/* Popular Posts */}
      {popularPosts.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-100 p-6 shadow-sm">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Articles</h3>
          <div className="space-y-3">
            {popularPosts.map((post, index) => (
              <a 
                key={index}
                href={post.slug}
                className="block group py-2 border-b border-gray-50 last:border-0"
              >
                <h4 className="text-sm font-medium text-gray-900 group-hover:text-primary transition-colors leading-snug">
                  {post.title}
                </h4>
                <p className="text-xs text-gray-500 mt-1">{post.category}</p>
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Categories */}
      {categories.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-100 p-6 shadow-sm">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Categories</h3>
          <div className="space-y-1">
            {categories.map((cat) => (
              <div 
                key={cat.name}
                className="flex items-center justify-between py-2 px-3 rounded-md hover:bg-gray-50 transition-colors"
              >
                <span className={category === cat.name ? 'font-semibold text-primary' : 'text-gray-700 text-sm'}>
                  {cat.name}
                </span>
                <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                  {cat.count}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

    </div>
  );
}